// Klient API panelu CRM. Sesja = token Basic w sessionStorage.
// W DEV (Vite, brak Functions) używa lokalnego mocka; w produkcji rozmawia z /api/admin/*.
import type {
  AdminLead,
  AnalyticsReport,
  ChatConversation,
  ChatMessageRow,
  GoogleAdsReport,
  LeadNote,
  LeadPriority,
  LeadStatus,
  LeadTask
} from "./types";

export type TaskInput = {
  title: string;
  dueAt: string;
  leadId?: string;
  owner?: string;
  priority?: LeadPriority;
};

export type TaskPatch = {
  title?: string;
  dueAt?: string;
  owner?: string;
  priority?: LeadPriority;
  done?: boolean;
};

const AUTH_KEY = "ochro-crm-auth";
const DEV = import.meta.env.DEV;

export class AuthError extends Error {}

type AuthState = { cred: string; user: string };

function readAuth(): AuthState | null {
  try {
    const raw = sessionStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    if (parsed && typeof parsed.cred === "string" && typeof parsed.user === "string") {
      return { cred: parsed.cred, user: parsed.user };
    }
  } catch {
    /* nieczytelna sesja — traktuj jak brak */
  }
  return null;
}

function writeAuth(state: AuthState) {
  sessionStorage.setItem(AUTH_KEY, JSON.stringify(state));
}

export function clearAuth() {
  sessionStorage.removeItem(AUTH_KEY);
}

export function currentUser(): string | null {
  return readAuth()?.user ?? null;
}

export function isAuthed(): boolean {
  return readAuth() !== null;
}

function emptyAnalyticsReport(days = 30): AnalyticsReport {
  const now = new Date();
  const daily = Array.from({ length: days }, (_, index) => {
    const date = new Date(now.getTime() - (days - 1 - index) * 86_400_000);
    const key = date.toISOString().slice(0, 10);
    return {
      date: key,
      label: `${key.slice(8, 10)}.${key.slice(5, 7)}`,
      events: 0,
      sessions: 0,
      leads: 0
    };
  });

  return {
    ok: true,
    days,
    since: daily[0]?.date || "",
    summary: {
      events: 0,
      sessions: 0,
      pageViews: 0,
      calculatorResults: 0,
      phoneClicks: 0,
      formStarts: 0,
      leads: 0,
      paidSessions: 0,
      paidLeads: 0,
      leadConversion: 0
    },
    funnel: [
      { key: "page_view", label: "Wejścia", value: 0 },
      { key: "oblicz_skladke", label: "Obliczenia", value: 0 },
      { key: "telefon_click", label: "Klik telefon", value: 0 },
      { key: "formularz_start", label: "Start formularza", value: 0 },
      { key: "lead_success", label: "Leady", value: 0 }
    ],
    daily,
    topPages: [],
    calculators: [],
    sources: [],
    devices: [],
    liveEvents: [],
    paidCampaigns: []
  };
}

function emptyGoogleAdsReport(days = 30): GoogleAdsReport {
  return {
    ok: true,
    configured: false,
    days,
    currency: "PLN",
    summary: {
      cost: 0,
      clicks: 0,
      impressions: 0,
      conversions: 0,
      ctr: 0,
      averageCpc: 0,
      costPerConversion: null
    },
    campaigns: []
  };
}

// Leniwy import mocka — w produkcji gałąź DEV jest usuwana, więc nie trafia do bundla.
let mockPromise: Promise<import("./mockStore").MockStore> | null = null;
function mock() {
  if (!mockPromise) mockPromise = import("./mockStore").then((m) => m.createMockStore());
  return mockPromise;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const auth = readAuth();
  if (!auth) throw new AuthError("NO_SESSION");
  const headers: Record<string, string> = {
    Authorization: `Basic ${auth.cred}`,
    ...(init?.headers as Record<string, string> | undefined)
  };
  if (init?.body) headers["Content-Type"] = "application/json";

  const res = await fetch(path, { ...init, headers });
  if (res.status === 401) {
    clearAuth();
    throw new AuthError("UNAUTHORIZED");
  }
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error((data.error as string) || `HTTP_${res.status}`);
  return data as T;
}

export async function login(username: string, password: string): Promise<string> {
  const cred = btoa(`${username}:${password}`);

  if (DEV) {
    const user = (await mock()).login(username, password);
    if (!user) throw new AuthError("INVALID_CREDENTIALS");
    writeAuth({ cred, user });
    return user;
  }

  const res = await fetch("/api/admin/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password })
  });
  if (res.status === 401) throw new AuthError("INVALID_CREDENTIALS");
  const data = (await res.json().catch(() => ({}))) as { user?: string; error?: string };
  if (!res.ok) throw new Error(data.error || `HTTP_${res.status}`);
  writeAuth({ cred, user: data.user || username });
  return data.user || username;
}

export function logout() {
  clearAuth();
}

export async function listLeads(): Promise<AdminLead[]> {
  if (!isAuthed()) throw new AuthError("NO_SESSION");
  if (DEV) return (await mock()).listLeads();
  const data = await request<{ leads: AdminLead[] }>("/api/admin/leads?limit=1000");
  return data.leads;
}

export async function getLead(id: string): Promise<{ lead: AdminLead; notes: LeadNote[] }> {
  if (DEV) {
    const { lead, notes } = (await mock()).getLead(id);
    if (!lead) throw new Error("NOT_FOUND");
    return { lead, notes };
  }
  const data = await request<{ lead: AdminLead; notes: LeadNote[] }>(
    `/api/admin/leads/${encodeURIComponent(id)}`
  );
  return { lead: data.lead, notes: data.notes };
}

export async function patchLead(
  id: string,
  patch: { status?: LeadStatus; owner?: string; priority?: LeadPriority }
): Promise<AdminLead> {
  if (DEV) {
    const lead = (await mock()).patchLead(id, patch);
    if (!lead) throw new Error("NOT_FOUND");
    return lead;
  }
  const data = await request<{ lead: AdminLead }>(`/api/admin/leads/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch)
  });
  return data.lead;
}

export async function addNote(id: string, body: string, author: string): Promise<LeadNote> {
  if (DEV) {
    const note = (await mock()).addNote(id, body, author);
    if (!note) throw new Error("EMPTY_NOTE");
    return note;
  }
  const data = await request<{ note: LeadNote }>(
    `/api/admin/leads/${encodeURIComponent(id)}/notes`,
    { method: "POST", body: JSON.stringify({ body, author }) }
  );
  return data.note;
}

export async function deleteLead(id: string): Promise<void> {
  if (DEV) {
    const ok = (await mock()).deleteLead(id);
    if (!ok) throw new Error("NOT_FOUND");
    return;
  }
  await request(`/api/admin/leads/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function listTasks(): Promise<LeadTask[]> {
  if (!isAuthed()) throw new AuthError("NO_SESSION");
  if (DEV) return (await mock()).listTasks();
  const data = await request<{ tasks: LeadTask[] }>("/api/admin/tasks");
  return data.tasks;
}

export async function createTask(input: TaskInput): Promise<LeadTask> {
  if (DEV) return (await mock()).createTask(input);
  const data = await request<{ task: LeadTask }>("/api/admin/tasks", {
    method: "POST",
    body: JSON.stringify(input)
  });
  return data.task;
}

export async function updateTask(id: string, patch: TaskPatch): Promise<LeadTask> {
  if (DEV) {
    const task = (await mock()).updateTask(id, patch);
    if (!task) throw new Error("NOT_FOUND");
    return task;
  }
  const data = await request<{ task: LeadTask }>(`/api/admin/tasks/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch)
  });
  return data.task;
}

export async function deleteTask(id: string): Promise<void> {
  if (DEV) {
    (await mock()).deleteTask(id);
    return;
  }
  await request(`/api/admin/tasks/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function listConversations(): Promise<ChatConversation[]> {
  if (!isAuthed()) throw new AuthError("NO_SESSION");
  if (DEV) return [];
  const data = await request<{ conversations: ChatConversation[] }>("/api/admin/chats?limit=300");
  return data.conversations;
}

export async function getAnalytics(days = 30): Promise<AnalyticsReport> {
  if (!isAuthed()) throw new AuthError("NO_SESSION");
  if (DEV) return emptyAnalyticsReport(days);
  return request<AnalyticsReport>(`/api/admin/analytics?days=${encodeURIComponent(String(days))}`);
}

export async function getGoogleAdsReport(days = 30): Promise<GoogleAdsReport> {
  if (!isAuthed()) throw new AuthError("NO_SESSION");
  if (DEV) return emptyGoogleAdsReport(days);
  return request<GoogleAdsReport>(`/api/admin/google-ads?days=${encodeURIComponent(String(days))}`);
}

export async function getConversation(
  id: string
): Promise<{ conversation: ChatConversation; messages: ChatMessageRow[] }> {
  if (DEV) {
    return {
      conversation: { id, createdAt: "", lastAt: "", ipCountry: "", userAgent: "", messageCount: 0, firstQuestion: "" },
      messages: []
    };
  }
  const data = await request<{ conversation: ChatConversation; messages: ChatMessageRow[] }>(
    `/api/admin/chats/${encodeURIComponent(id)}`
  );
  return { conversation: data.conversation, messages: data.messages };
}

export async function deleteConversation(id: string): Promise<void> {
  if (DEV) return;
  await request(`/api/admin/chats/${encodeURIComponent(id)}`, { method: "DELETE" });
}
